// FEAT-6 — the page's handle on the JavaScript / SQL engine worker (worker.ts).
//
// One worker per language, spawned lazily. A run that outlives its deadline
// gets the worker terminate()d — the only way to stop a spinning program, and
// for SQL the only CPU stop there is — and the next run spawns a fresh one.
// SQL also takes a FRESH worker for every run, as the server grader does:
// SQLite's heap limit is process-global and a candidate may lower it, so a
// reused worker would carry one answer's setting into the next — in the
// recruiter's browser, into ANOTHER candidate's grade.
import type { RunResult } from "@/lib/pyodide";
import type { WorkerLanguage, WorkerReply, WorkerRequest } from "./protocol";

// Host-side stops. JavaScript is normally interrupted INSIDE the jail at 2 s
// (+1 s for the expected-output oracle); this is the backstop. SQL has no
// in-engine stop, so its deadline is the grader's 3 s.
const RUN_DEADLINE_MS: Record<WorkerLanguage, number> = {
  javascript: 5000,
  sql: 3000,
  typescript: 5000, // FEAT-12: stripped, then JavaScript's in-jail stops
};

type RunRequest = Omit<Extract<WorkerRequest, { kind: "run" }>, "id" | "kind" | "language">;

class EngineWorker {
  private worker: Worker | null = null;
  private ready: Promise<void> | null = null;
  private seq = 0;
  private readonly pending = new Map<number, (reply: WorkerReply) => void>();

  constructor(private readonly language: WorkerLanguage) {}

  /** Spawn + load the engine. A failure is not cached: the next call retries. */
  warm(): Promise<void> {
    if (this.ready) return this.ready;
    const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (event: MessageEvent<WorkerReply>) => {
      const settle = this.pending.get(event.data.id);
      if (settle) {
        this.pending.delete(event.data.id);
        settle(event.data);
      }
    };
    worker.onerror = (event) => {
      event.preventDefault();
      this.fail(`The ${this.language} sandbox stopped: ${event.message || "worker error"}`);
    };
    this.worker = worker;
    const attempt = this.send({ kind: "warm", language: this.language }).then((reply) => {
      if (!reply.ok) throw new Error(reply.error);
    });
    this.ready = attempt;
    attempt.catch(() => {
      if (this.ready === attempt) this.kill();
    });
    return attempt;
  }

  async run(request: RunRequest): Promise<RunResult> {
    await this.warm();
    const reply = await new Promise<WorkerReply | "timeout">((resolve) => {
      const timer = setTimeout(() => resolve("timeout"), RUN_DEADLINE_MS[this.language]);
      void this.send({ kind: "run", language: this.language, ...request }).then((r) => {
        clearTimeout(timer);
        resolve(r);
      });
    });
    if (reply === "timeout" || this.language === "sql") this.kill();
    if (reply === "timeout") {
      return {
        passed: false,
        output: "",
        error: `Time limit exceeded — stopped after ${RUN_DEADLINE_MS[this.language] / 1000} seconds.`,
      };
    }
    if (!reply.ok || !reply.result) {
      this.kill();
      throw new Error(reply.ok ? "The sandbox returned no result" : reply.error);
    }
    return reply.result;
  }

  private send(message: Omit<WorkerRequest, "id">): Promise<WorkerReply> {
    const id = ++this.seq;
    return new Promise((resolve) => {
      this.pending.set(id, resolve);
      this.worker?.postMessage({ ...message, id } as WorkerRequest);
    });
  }

  private fail(error: string): void {
    const waiting = [...this.pending.entries()];
    this.pending.clear();
    for (const [id, settle] of waiting) settle({ id, ok: false, error });
    this.kill();
  }

  private kill(): void {
    this.worker?.terminate();
    this.worker = null;
    this.ready = null;
    // Anything still waiting belonged to the terminated worker.
    const waiting = [...this.pending.entries()];
    this.pending.clear();
    for (const [id, settle] of waiting) {
      settle({ id, ok: false, error: "The sandbox was restarted" });
    }
  }
}

const engines: Partial<Record<WorkerLanguage, EngineWorker>> = {};

export function engineFor(language: WorkerLanguage): EngineWorker {
  return (engines[language] ??= new EngineWorker(language));
}
